import { ethers } from 'ethers';
import { getNetworkConfig } from '@/config/networks';
import DORStablecoinABI from './abis/DORStablecoin.json';

export class DORContract {
  private contract: ethers.Contract;
  private readOnlyContract: ethers.Contract;
  private provider: ethers.Provider;
  private signer?: ethers.Signer;

  constructor(chainId: number, signer?: ethers.Signer) {
    const network = getNetworkConfig(chainId);
    this.provider = new ethers.JsonRpcProvider(network.rpcUrl);
    this.signer = signer;
    
    // 对于只读操作使用 provider，对于写操作使用 signer
    this.contract = new ethers.Contract(
      network.contracts.dorToken,
      DORStablecoinABI.abi,
      signer || this.provider
    );
    
    // 创建专门用于只读操作的合约实例
    this.readOnlyContract = new ethers.Contract(
      network.contracts.dorToken,
      DORStablecoinABI.abi,
      this.provider
    );
  }

  // 获取合约实例
  getContract(): ethers.Contract {
    return this.contract;
  }

  // 获取DOR代币信息
  async getTokenInfo() {
    try {
      // 逐个调用方法，避免一个失败影响其他
      let name = 'DOR Stablecoin';
      let symbol = 'DOR';
      let decimals = 18;
      let totalSupply = '0';

      try {
        name = await this.readOnlyContract.name();
        await new Promise(resolve => setTimeout(resolve, 100)); // 添加100ms延迟
      } catch (error) {
        console.warn('获取代币名称失败，使用默认值:', error);
      }

      try {
        symbol = await this.readOnlyContract.symbol();
        await new Promise(resolve => setTimeout(resolve, 100)); // 添加100ms延迟
      } catch (error) {
        console.warn('获取代币符号失败，使用默认值:', error);
      }

      try {
        decimals = await this.readOnlyContract.decimals();
        await new Promise(resolve => setTimeout(resolve, 100)); // 添加100ms延迟
      } catch (error) {
        console.warn('获取代币精度失败，使用默认值18:', error);
      }

      try {
        const totalSupplyRaw = await this.readOnlyContract.totalSupply();
        totalSupply = ethers.formatUnits(totalSupplyRaw, decimals);
      } catch (error) {
        console.warn('获取总供应量失败，使用默认值0:', error);
      }

      return {
        name,
        symbol,
        decimals,
        totalSupply,
      };
    } catch (error) {
      console.warn('获取代币信息失败，返回默认值:', error);
      // 返回默认值而不是抛出错误
      return {
        name: 'DOR Stablecoin',
        symbol: 'DOR',
        decimals: 18,
        totalSupply: '0',
      };
    }
  }

  // 获取用户DOR余额
  async getBalance(address: string): Promise<string> {
    try {
      const balance = await this.readOnlyContract.balanceOf(address);
      const decimals = await this.readOnlyContract.decimals();
      return ethers.formatUnits(balance, decimals);
    } catch (error) {
      console.warn('获取余额失败，返回0:', error);
      return '0'; // 返回0而不是抛出错误
    }
  }

  // 获取用户流通DOR余额
  async getCirculatingBalance(address: string): Promise<string> {
    try {
      const balance = await this.readOnlyContract.getCirculatingBalance(address);
      const decimals = await this.readOnlyContract.decimals();
      return ethers.formatUnits(balance, decimals);
    } catch (error) {
      console.warn('获取流通余额失败，返回0:', error);
      return '0'; // 返回0而不是抛出错误
    }
  }

  // 获取用户邀请信息
  async getInviter(address: string): Promise<string> {
    try {
      const userInfo = await this.readOnlyContract.getUserInfo(address);
      return userInfo.inviter;
    } catch (error) {
      console.warn('获取邀请人失败，返回零地址:', error);
      return '0x0000000000000000000000000000000000000000'; // 返回零地址而不是抛出错误
    }
  }

  // 设置邀请人
  async setInviter(inviterAddress: string): Promise<ethers.ContractTransaction> {
    if (!this.signer) {
      throw new Error('需要签名者来执行此操作');
    }

    try {
      const tx = await this.contract.setInviter(inviterAddress);
      return tx;
    } catch (error) {
      console.error('设置邀请人失败:', error);
      throw error;
    }
  }

  // 充值USDT获取DOR
  async recharge(usdtAmount: string): Promise<ethers.ContractTransaction> {
    if (!this.signer) {
      throw new Error('需要签名者来执行此操作');
    }

    try {
      const decimals = await this.contract.decimals();
      const amount = ethers.parseUnits(usdtAmount, decimals);
      const tx = await this.contract.deposit(amount);
      return tx;
    } catch (error) {
      console.error('充值失败:', error);
      throw error;
    }
  }

  // 带邀请人的充值
  async rechargeWithInviter(usdtAmount: string, inviterAddress: string): Promise<ethers.ContractTransaction> {
    if (!this.signer) {
      throw new Error('需要签名者来执行此操作');
    }

    try {
      const decimals = await this.contract.decimals();
      const amount = ethers.parseUnits(usdtAmount, decimals);
      const tx = await this.contract.depositWithInviter(amount, inviterAddress);
      return tx;
    } catch (error) {
      console.error('带邀请人的充值失败:', error);
      throw error;
    }
  }

  // 赎回DOR获取USDT
  async redeem(dorAmount: string): Promise<ethers.ContractTransaction> {
    if (!this.signer) {
      throw new Error('需要签名者来执行此操作');
    }

    try {
      const decimals = await this.contract.decimals();
      const amount = ethers.parseUnits(dorAmount, decimals);
      const tx = await this.contract.redeem(amount);
      return tx;
    } catch (error) {
      console.error('赎回失败:', error);
      throw error;
    }
  }

  // 获取价格信息 - 通过流动性余额计算
  async getPrice(): Promise<string> {
    try {
      const [totalSupply, liquidityBalance] = await Promise.all([
        this.readOnlyContract.totalSupply(),
        this.readOnlyContract.getLiquidityDORBalance(),
      ]);
      
      // 计算价格：流动性余额 / 总供应量
      const price = Number(liquidityBalance) > 0 ? 
        Number(liquidityBalance) / Number(totalSupply) : 1.0;
      
      return price.toString();
    } catch (error) {
      console.warn('获取价格失败，返回默认价格1.0:', error);
      return '1.0'; // 返回默认价格而不是抛出错误
    }
  }

  // 获取用户邀请奖励
  async getInviteReward(address: string): Promise<string> {
    try {
      // userRewards 是映射，不是函数，需要通过 ABI 调用
      const reward = await this.readOnlyContract.userRewards(address);
      const decimals = await this.readOnlyContract.decimals();
      return ethers.formatUnits(reward, decimals);
    } catch (error) {
      console.warn('获取邀请奖励失败，返回0:', error);
      return '0'; // 返回0而不是抛出错误
    }
  }

  // 提取邀请奖励 - 合约中没有此方法，暂时返回错误
  async claimInviteReward(): Promise<ethers.ContractTransaction> {
    throw new Error('合约中暂未实现提取邀请奖励功能');
  }

  // 获取用户锁定信息
  async getLockInfo(address: string) {
    try {
      const userInfo = await this.readOnlyContract.getUserInfo(address);
      const decimals = await this.readOnlyContract.decimals();
      
      return {
        lockedAmount: ethers.formatUnits(userInfo.lockedAmount, decimals),
        unlockTime: new Date(), // 合约中没有解锁时间，使用当前时间
        isLocked: Number(userInfo.lockedAmount) > 0,
      };
    } catch (error) {
      console.warn('获取锁定信息失败，返回默认值:', error);
      // 返回默认值而不是抛出错误
      return {
        lockedAmount: '0',
        unlockTime: new Date(),
        isLocked: false,
      };
    }
  }

  // 锁定DOR - 合约中没有此方法，暂时返回错误
  async lockDOR(amount: string, lockTime: number): Promise<ethers.ContractTransaction> {
    throw new Error('合约中暂未实现锁定DOR功能');
  }

  // 解锁DOR - 合约中没有此方法，暂时返回错误
  async unlockDOR(): Promise<ethers.ContractTransaction> {
    throw new Error('合约中暂未实现解锁DOR功能');
  }

  // 获取合约统计信息
  async getStats() {
    try {
      // 获取总供应量
      let totalSupply = '0';
      try {
        const totalSupplyRaw = await this.readOnlyContract.totalSupply();
        const decimals = await this.readOnlyContract.decimals();
        totalSupply = ethers.formatUnits(totalSupplyRaw, decimals);
      } catch (error) {
        console.warn('获取总供应量失败:', error);
      }

      // 获取总锁定量（状态变量，不是函数）
      let totalLocked = '0';
      try {
        const totalLockedRaw = await this.readOnlyContract.totalLocked();
        const decimals = await this.readOnlyContract.decimals();
        totalLocked = ethers.formatUnits(totalLockedRaw, decimals);
      } catch (error) {
        console.warn('获取总锁定量失败，使用默认值0:', error);
      }

      // 获取流动性余额
      let liquidityBalance = '0';
      try {
        const liquidityBalanceRaw = await this.readOnlyContract.getLiquidityDORBalance();
        const decimals = await this.readOnlyContract.decimals();
        liquidityBalance = ethers.formatUnits(liquidityBalanceRaw, decimals);
      } catch (error) {
        console.warn('获取流动性余额失败，使用默认值0:', error);
      }

      // 计算价格：流动性余额 / 总供应量
      const price = Number(liquidityBalance) > 0 && Number(totalSupply) > 0 ? 
        Number(liquidityBalance) / Number(totalSupply) : 1.0;
      
      return {
        totalSupply,
        totalLocked,
        price: price.toString(),
      };
    } catch (error) {
      console.warn('获取统计信息失败，返回默认值:', error);
      // 返回默认值而不是抛出错误
      return {
        totalSupply: '0',
        totalLocked: '0',
        price: '1.0',
      };
    }
  }

  // USDT 授权
  async approveUSDT(usdtAddress: string, dorAddress: string, amount: string): Promise<ethers.ContractTransaction> {
    if (!this.signer) {
      throw new Error('需要签名者来执行此操作');
    }
    
    const usdtAbi = [
      'function approve(address spender, uint256 amount) external returns (bool)',
      'function allowance(address owner, address spender) external view returns (uint256)',
    ];
    
    try {
      const usdtContract = new ethers.Contract(usdtAddress, usdtAbi, this.signer);
      
      // BSC测试网USDT使用18位小数
      const decimals = 18;
      console.log('USDT decimals:', decimals);
      
      const approveAmount = ethers.parseUnits(amount, decimals);
      console.log('USDT approve amount:', approveAmount.toString());
      console.log('USDT 合约地址:', usdtAddress);
      console.log('DOR 合约地址:', dorAddress);
      
      const tx = await usdtContract.approve(dorAddress, approveAmount);
      return tx;
    } catch (error) {
      console.error('USDT approve 失败:', error);
      console.error('USDT 合约地址:', usdtAddress);
      console.error('DOR 合约地址:', dorAddress);
      console.error('批准金额:', amount);
      throw error;
    }
  }
}

// 创建合约实例的工厂函数
export const createDORContract = (chainId: number, signer?: ethers.Signer): DORContract => {
  return new DORContract(chainId, signer);
}; 